import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "react-toastify";
import { inspectionTypesService } from "../services/inspectionTypesService";
import styles from "../assets/inspectionTypeCreate.module.css";

const InspectionTypeCreate = () => {
  const navigate = useNavigate();

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    color: "",
  });

  // UI state
  const [loading, setLoading] = useState(false);
  const [useMockData, setUseMockData] = useState(false);
  const [errors, setErrors] = useState({});
  const [showColorPicker, setShowColorPicker] = useState(false);

  // Predefined colors for quick selection
  const predefinedColors = [
    "#ff0000",
    "#00ff00",
    "#0000ff",
    "#ffff00",
    "#ff00ff",
    "#00ffff",
    "#ffa500",
    "#800080",
    "#008000",
    "#ffc0cb",
    "#a52a2a",
    "#808080",
    "#000000",
    "#ffffff",
    "#5bc0de",
    "#5cb85c",
    "#d9534f",
    "#f0ad4e",
    "#337ab7",
    "#6f42c1",
    "#20c997",
    "#fd7e14",
  ];

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  // Handle color selection
  const handleColorSelect = (color) => {
    setFormData((prev) => ({
      ...prev,
      color: color,
    }));
    setShowColorPicker(false);

    if (errors.color) {
      setErrors((prev) => ({
        ...prev,
        color: "",
      }));
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    } else if (formData.name.length > 255) {
      newErrors.name = "Name must not exceed 255 characters";
    }

    // Color validation is optional, but if provided, validate format
    if (
      formData.color &&
      !/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(formData.color)
    ) {
      newErrors.color = "Please enter a valid hex color (e.g., #ff0000)";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      if (useMockData) {
        // Simulate API call
        await new Promise((resolve) => setTimeout(resolve, 1000));
        toast.success("Inspection Type created successfully! (mock)");
      } else {
        await inspectionTypesService.createInspectionType(formData);
        toast.success("Inspection Type created successfully!");
      }

      // Redirect to index page
      navigate("/inspection-types");
    } catch (error) {
      console.error("Error creating inspection type:", error);

      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      } else {
        toast.error("Failed to create inspection type. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    navigate("/inspection-types");
  };

  return (
    <div className={styles.container}>
      {/* Breadcrumbs */}
      <div className={styles.breadcrumbs}>
        <Link to="/dashboard">Dashboard</Link>
        <span className={styles.separator}>/</span>
        <Link to="/inspection-types">Inspection Types</Link>
        <span className={styles.separator}>/</span>
        <span className={styles.currentPage}>Create</span>
      </div>

      {/* Mock Data Toggle (for development) */}
      <div className={styles.mockDataToggle}>
        <label>
          <input
            type="checkbox"
            checked={useMockData}
            onChange={(e) => setUseMockData(e.target.checked)}
          />
          Use Mock Data
        </label>
      </div>

      <div className={styles.row}>
        <div className={styles.colMd12}>
          <div className={styles.panel}>
            <div className={styles.panelHeading}>
              <h3>Create Inspection Type</h3>
            </div>
            <div className={styles.panelBody}>
              <form onSubmit={handleSubmit}>
                <fieldset className={styles.formHorizontal}>
                  {/* Name Field */}
                  <div className={styles.formGroup}>
                    <label
                      className={`${styles.controlLabel} ${styles.colSm3} ${styles.labelRequired}`}
                    >
                      Name
                    </label>
                    <div className={styles.colSm5}>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        className={`${styles.formControl} ${
                          errors.name ? styles.hasError : ""
                        }`}
                        required
                        placeholder="Enter inspection type name"
                      />
                      {errors.name && (
                        <div className={styles.errorMessage}>{errors.name}</div>
                      )}
                    </div>
                  </div>

                  {/* Color Field */}
                  <div className={styles.formGroup}>
                    <label
                      className={`${styles.controlLabel} ${styles.colSm3}`}
                    >
                      Color
                    </label>
                    <div className={styles.colMd5}>
                      <div className={styles.inputGroup}>
                        <input
                          type="text"
                          name="color"
                          value={formData.color}
                          onChange={handleInputChange}
                          className={`${styles.formControl} ${
                            styles.colorInput
                          } ${errors.color ? styles.hasError : ""}`}
                          placeholder="#000000"
                          onFocus={() => setShowColorPicker(true)}
                        />
                        <span
                          className={styles.inputGroupAddon}
                          onClick={() => setShowColorPicker(!showColorPicker)}
                          style={{ cursor: "pointer" }}
                        >
                          <i
                            className={styles.colorPreview}
                            style={{
                              backgroundColor: formData.color || "#ccc",
                            }}
                          ></i>
                        </span>
                      </div>

                      {/* Color Picker Dropdown */}
                      {showColorPicker && (
                        <div className={styles.colorPickerDropdown}>
                          <div className={styles.colorPickerHeader}>
                            <span>Select a color:</span>
                            <button
                              type="button"
                              className={styles.closeButton}
                              onClick={() => setShowColorPicker(false)}
                            >
                              ×
                            </button>
                          </div>
                          <div className={styles.colorGrid}>
                            {predefinedColors.map((color, index) => (
                              <div
                                key={index}
                                className={styles.colorOption}
                                style={{ backgroundColor: color }}
                                onClick={() => handleColorSelect(color)}
                                title={color}
                              ></div>
                            ))}
                          </div>
                          <div className={styles.colorPickerFooter}>
                            <input
                              type="text"
                              value={formData.color}
                              onChange={handleInputChange}
                              name="color"
                              placeholder="Or enter hex color"
                              className={styles.hexInput}
                            />
                          </div>
                        </div>
                      )}

                      {errors.color && (
                        <div className={styles.errorMessage}>
                          {errors.color}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Submit Button */}
                  <div className={styles.formGroup}>
                    <div className={styles.colSm12}>
                      <div className={styles.textRight}>
                        <button
                          style={{ backgroundColor: "#1B2046" }}
                          type="submit"
                          className={styles.btnHead}
                          disabled={loading}
                        >
                          {loading ? "Creating..." : "Create"}
                        </button>
                      </div>
                    </div>
                  </div>
                </fieldset>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InspectionTypeCreate;
